import React, { useState, useEffect } from 'react';
import { 
  Search, Plus, Edit, Trash2, Loader, X, AlertCircle, CheckCircle,
  RefreshCw, Shield, Key, Clock, Ban, TrendingUp, Users, 
  Settings, Save, Eye, AlertTriangle
} from 'lucide-react';
import adminService, { 
  OTPSecurityRule, OTPSecurityStatistics, FailedOTPAttempt,
  FailedOTPAttemptsResponse, CreateOTPRuleRequest, UpdateOTPRuleRequest,
  GetFailedOTPAttemptsParams, OTPRuleType, OTPAppliesTo
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

type TabType = 'overview' | 'rules' | 'failed-attempts';

const OTPSecurityManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Statistics
  const [otpStats, setOtpStats] = useState<OTPSecurityStatistics | null>(null);

  // Rules
  const [rules, setRules] = useState<OTPSecurityRule[]>([]);
  const [ruleModal, setRuleModal] = useState<'create' | 'edit' | null>(null);
  const [deleteRuleModal, setDeleteRuleModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<OTPSecurityRule | null>(null);
  const [ruleForm, setRuleForm] = useState<CreateOTPRuleRequest>({
    rule_name: '',
    rule_type: 'max_attempts',
    rule_value: '',
    description: '',
    is_active: true,
    applies_to: 'all',
  });

  // Failed Attempts
  const [failedAttempts, setFailedAttempts] = useState<FailedOTPAttempt[]>([]);
  const [attemptsPage, setAttemptsPage] = useState(1);
  const [attemptsTotalPages, setAttemptsTotalPages] = useState(1);
  const [attemptsTotal, setAttemptsTotal] = useState(0);
  const [attemptsFilters, setAttemptsFilters] = useState<GetFailedOTPAttemptsParams>({
    page: 1,
    limit: 20,
  });

  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchOTPStats();
    if (activeTab === 'rules') {
      fetchRules();
    }
    if (activeTab === 'failed-attempts') {
      fetchFailedAttempts();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'failed-attempts') {
      fetchFailedAttempts();
    }
  }, [attemptsPage, attemptsFilters]);

  const fetchOTPStats = async () => {
    try {
      setLoading(true);
      const stats = await adminService.getOTPSecurityStatistics();
      setOtpStats(stats);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch OTP statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    try {
      setLoading(true);
      const data = await adminService.getOTPSecurityRules();
      setRules(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch OTP rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchFailedAttempts = async () => {
    try {
      setLoading(true);
      const data = await adminService.getFailedOTPAttempts({
        ...attemptsFilters,
        page: attemptsPage,
      });
      setFailedAttempts(data.attempts);
      setAttemptsPage(data.pagination.current_page);
      setAttemptsTotalPages(data.pagination.total_pages);
      setAttemptsTotal(data.pagination.total_attempts);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch failed attempts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await adminService.createOTPSecurityRule(ruleForm);
      toast.success('OTP rule created successfully!');
      setRuleModal(null);
      resetRuleForm();
      fetchRules();
      fetchOTPStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create OTP rule');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRule) return;
    
    try {
      setProcessing(true);
      const updateData: UpdateOTPRuleRequest = {
        rule_id: selectedRule.rule_id,
        ...ruleForm,
      };
      await adminService.updateOTPSecurityRule(updateData);
      toast.success('OTP rule updated successfully!');
      setRuleModal(null);
      resetRuleForm();
      fetchRules();
      fetchOTPStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update OTP rule');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!selectedRule) return;
    
    try {
      setProcessing(true);
      await adminService.deleteOTPSecurityRule(selectedRule.rule_id);
      toast.success('OTP rule deleted successfully!');
      setDeleteRuleModal(false);
      setSelectedRule(null);
      fetchRules();
      fetchOTPStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete OTP rule');
    } finally {
      setProcessing(false);
    }
  };

  const openEditRule = (rule: OTPSecurityRule) => {
    setSelectedRule(rule);
    let ruleValue: string | number | string[] = rule.rule_value;
    try {
      const parsed = JSON.parse(rule.rule_value);
      if (Array.isArray(parsed)) {
        ruleValue = parsed;
      }
    } catch {
      // Not JSON, keep as string
    }
    
    setRuleForm({
      rule_name: rule.rule_name,
      rule_type: rule.rule_type,
      rule_value: ruleValue,
      description: rule.description || '',
      is_active: rule.is_active === 1,
      applies_to: rule.applies_to,
    });
    setRuleModal('edit');
  };

  const openDeleteRule = (rule: OTPSecurityRule) => {
    setSelectedRule(rule);
    setDeleteRuleModal(true);
  };

  const resetRuleForm = () => {
    setRuleForm({
      rule_name: '',
      rule_type: 'max_attempts',
      rule_value: '',
      description: '',
      is_active: true,
      applies_to: 'all',
    });
    setSelectedRule(null);
  };

  const getRuleTypeLabel = (type: OTPRuleType) => {
    const labels: Record<OTPRuleType, string> = {
      max_attempts: 'Max Attempts',
      expiry_time: 'Expiry Time',
      resend_interval: 'Resend Interval',
      block_duration: 'Block Duration',
      ip_whitelist: 'IP Whitelist',
      ip_blacklist: 'IP Blacklist',
    };
    return labels[type] || type;
  };

  const getRuleValueDisplay = (rule: OTPSecurityRule) => {
    try {
      const parsed = JSON.parse(rule.rule_value);
      if (Array.isArray(parsed)) {
        return parsed.join(', ');
      }
    } catch {
      // Not JSON
    }
    if (rule.rule_type === 'expiry_time' || rule.rule_type === 'block_duration' || rule.rule_type === 'resend_interval') {
      const seconds = parseInt(rule.rule_value);
      if (!isNaN(seconds)) {
        if (seconds < 60) return `${seconds} seconds`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
        return `${Math.floor(seconds / 3600)} hours`;
      }
    }
    return rule.rule_value;
  };

  const filteredRules = rules.filter(rule =>
    rule.rule_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rule.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rule.rule_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const ruleTypeOptions: OTPRuleType[] = [
    'max_attempts', 'expiry_time', 'resend_interval', 
    'block_duration', 'ip_whitelist', 'ip_blacklist'
  ];

  const appliesToOptions: OTPAppliesTo[] = ['all', 'user', 'business', 'admin'];

  if (loading && activeTab === 'overview') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">OTP Security Management</h1>
          <p className="text-secondary-600">Manage OTP security rules and monitor failed attempts</p>
        </div>
        <button
          onClick={() => {
            fetchOTPStats();
            if (activeTab === 'rules') fetchRules();
            if (activeTab === 'failed-attempts') fetchFailedAttempts();
          }}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all disabled:opacity-50"
        >
          <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="border-b border-secondary-200">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <TrendingUp size={20} />
              <span>Overview</span>
            </button>
            <button
              onClick={() => setActiveTab('rules')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'rules'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <Settings size={20} />
              <span>Security Rules</span>
            </button>
            <button
              onClick={() => setActiveTab('failed-attempts')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'failed-attempts'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <AlertTriangle size={20} />
              <span>Failed Attempts</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {otpStats ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-blue-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">Total Rules</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">{otpStats.total_rules}</p>
                        </div>
                        <Settings className="text-blue-500" size={32} />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-green-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">Active Rules</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">{otpStats.active_rules}</p>
                        </div>
                        <CheckCircle className="text-green-500" size={32} />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-yellow-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">Inactive Rules</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">{otpStats.inactive_rules}</p>
                        </div>
                        <Ban className="text-yellow-500" size={32} />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-red-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">Failed Attempts (Today)</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">{otpStats.today_failed_attempts}</p>
                        </div>
                        <AlertTriangle className="text-red-500" size={32} />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertTriangle className="text-yellow-500 mb-4" size={48} />
                  <p className="text-secondary-600 text-lg font-semibold mb-2">Unable to load OTP statistics</p>
                  <p className="text-secondary-500 text-sm">Please try refreshing the page</p>
                </div>
              )}
            </>
          )}

          {/* Rules Tab */}
          {activeTab === 'rules' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search rules..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <button
                  onClick={() => {
                    resetRuleForm();
                    setRuleModal('create');
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all"
                >
                  <Plus size={20} />
                  <span>Create Rule</span>
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="animate-spin text-primary-500" size={32} />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredRules.length === 0 ? (
                    <div className="col-span-full text-center py-12">
                      <AlertCircle className="mx-auto text-secondary-400 mb-4" size={48} />
                      <p className="text-secondary-600 font-medium">No rules found</p>
                    </div>
                  ) : (
                    filteredRules.map((rule) => (
                      <div key={rule.rule_id} className="bg-white border-2 border-secondary-200 rounded-xl p-6 hover:border-primary-300 transition-all">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-secondary-900 mb-1">{rule.rule_name}</h3>
                            <p className="text-xs text-secondary-500 mb-2">{getRuleTypeLabel(rule.rule_type)}</p>
                            {rule.description && (
                              <p className="text-sm text-secondary-600">{rule.description}</p>
                            )}
                          </div>
                          {rule.is_active === 1 ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Active</span>
                          ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Inactive</span>
                          )}
                        </div>
                        <div className="mb-4">
                          <p className="text-xs text-secondary-500 mb-1">Value</p>
                          <p className="font-semibold text-secondary-900">{getRuleValueDisplay(rule)}</p>
                          <p className="text-xs text-secondary-500 mt-1">Applies to: {rule.applies_to}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openEditRule(rule)}
                            className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg font-semibold hover:bg-blue-100 transition-all text-sm"
                          >
                            <Edit size={16} className="inline mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => openDeleteRule(rule)}
                            className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition-all text-sm"
                          >
                            <Trash2 size={16} className="inline mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Failed Attempts Tab */}
          {activeTab === 'failed-attempts' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <select
                  value={attemptsFilters.entity_type || ''}
                  onChange={(e) => setAttemptsFilters({
                    ...attemptsFilters, 
                    entity_type: e.target.value as 'user' | 'business' | 'admin' || undefined,
                    page: 1
                  })}
                  className="px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                >
                  <option value="">All Types</option>
                  <option value="user">User</option>
                  <option value="business">Business</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="animate-spin text-primary-500" size={32} />
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Entity</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Contact</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">IP Address</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Failure Reason</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Attempted At</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-secondary-200">
                        {failedAttempts.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-12 text-center text-secondary-600">
                              No failed attempts found
                            </td>
                          </tr>
                        ) : (
                          failedAttempts.map((attempt) => (
                            <tr key={attempt.attempt_id} className="hover:bg-secondary-50 transition-colors">
                              <td className="px-6 py-4">
                                <div>
                                  <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold capitalize">
                                    {attempt.entity_type}
                                  </span>
                                  {attempt.entity_name && (
                                    <p className="text-sm text-secondary-900 mt-1">{attempt.entity_name}</p>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div>
                                  {attempt.phone_number && (
                                    <p className="text-sm text-secondary-900">{attempt.phone_number}</p>
                                  )}
                                  {attempt.email && (
                                    <p className="text-sm text-secondary-600">{attempt.email}</p>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <code className="text-sm font-mono text-secondary-600">{attempt.ip_address}</code>
                              </td>
                              <td className="px-6 py-4">
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                                  {attempt.failure_reason}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-secondary-600">
                                  {new Date(attempt.attempted_at).toLocaleString()}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {attemptsTotalPages > 1 && (
                    <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-between">
                      <p className="text-sm text-secondary-600">
                        Page {attemptsPage} of {attemptsTotalPages} ({attemptsTotal} total)
                      </p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setAttemptsPage(p => Math.max(1, p - 1))}
                          disabled={attemptsPage === 1}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setAttemptsPage(p => Math.min(attemptsTotalPages, p + 1))}
                          disabled={attemptsPage === attemptsTotalPages}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Rule Modal */}
      {ruleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-secondary-900">
                  {ruleModal === 'create' ? 'Create OTP Rule' : 'Edit OTP Rule'}
                </h2>
                <button
                  onClick={() => {
                    setRuleModal(null);
                    resetRuleForm();
                  }}
                  className="text-secondary-500 hover:text-secondary-700"
                >
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={ruleModal === 'create' ? handleCreateRule : handleUpdateRule} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Rule Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={ruleForm.rule_name}
                    onChange={(e) => setRuleForm({...ruleForm, rule_name: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter rule name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Rule Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={ruleForm.rule_type}
                    onChange={(e) => {
                      setRuleForm({
                        ...ruleForm, 
                        rule_type: e.target.value as OTPRuleType,
                        rule_value: ''
                      });
                    }}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    {ruleTypeOptions.map(type => (
                      <option key={type} value={type}>{getRuleTypeLabel(type)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Rule Value <span className="text-red-500">*</span>
                  </label>
                  {(ruleForm.rule_type === 'ip_whitelist' || ruleForm.rule_type === 'ip_blacklist') ? (
                    <textarea
                      required
                      value={Array.isArray(ruleForm.rule_value) ? ruleForm.rule_value.join('\n') : ruleForm.rule_value}
                      onChange={(e) => {
                        const lines = e.target.value.split('\n').filter(line => line.trim());
                        setRuleForm({...ruleForm, rule_value: lines});
                      }}
                      className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none font-mono"
                      rows={4}
                      placeholder="Enter IP addresses (one per line)"
                    />
                  ) : (
                    <input
                      type={ruleForm.rule_type === 'max_attempts' ? 'number' : 'number'}
                      required
                      value={typeof ruleForm.rule_value === 'string' && !Array.isArray(ruleForm.rule_value) ? ruleForm.rule_value : ''}
                      onChange={(e) => setRuleForm({...ruleForm, rule_value: e.target.value})}
                      className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                      placeholder={ruleForm.rule_type === 'expiry_time' || ruleForm.rule_type === 'block_duration' || ruleForm.rule_type === 'resend_interval' ? 'Enter value in seconds' : 'Enter value'}
                    />
                  )}
                  <p className="text-xs text-secondary-500 mt-1">
                    {ruleForm.rule_type === 'expiry_time' || ruleForm.rule_type === 'block_duration' || ruleForm.rule_type === 'resend_interval' 
                      ? 'Value in seconds (e.g., 300 for 5 minutes)' 
                      : ruleForm.rule_type === 'ip_whitelist' || ruleForm.rule_type === 'ip_blacklist'
                      ? 'Enter IP addresses, one per line'
                      : 'Enter numeric value'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Description</label>
                  <textarea
                    value={ruleForm.description || ''}
                    onChange={(e) => setRuleForm({...ruleForm, description: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={3}
                    placeholder="Enter rule description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Applies To <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={ruleForm.applies_to}
                    onChange={(e) => setRuleForm({...ruleForm, applies_to: e.target.value as OTPAppliesTo})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    {appliesToOptions.map(option => (
                      <option key={option} value={option}>{option.charAt(0).toUpperCase() + option.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={ruleForm.is_active}
                      onChange={(e) => setRuleForm({...ruleForm, is_active: e.target.checked})}
                      className="w-4 h-4 text-primary-500 border-secondary-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-semibold text-secondary-700">Active</span>
                  </label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setRuleModal(null);
                      resetRuleForm();
                    }}
                    className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-6 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                  >
                    {processing ? 'Saving...' : ruleModal === 'create' ? 'Create Rule' : 'Update Rule'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Rule Modal */}
      {deleteRuleModal && selectedRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="text-red-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-secondary-900">Delete Rule</h2>
                  <p className="text-sm text-secondary-600">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-secondary-700 mb-6">
                Are you sure you want to delete the rule <strong>"{selectedRule.rule_name}"</strong>?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setDeleteRuleModal(false);
                    setSelectedRule(null);
                  }}
                  className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteRule}
                  disabled={processing}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-all disabled:opacity-50"
                >
                  {processing ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OTPSecurityManagement;

